/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

import { Component, Input, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { Subject } from 'rxjs';
import { APIError } from '@support/api/common/error.interface';
import { AppConfig } from '@root/app.config';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { GlobalErrorSubjectInterface } from '@support/shared/widgets/interfaces/global-error-message.interface';
import { takeUntil } from 'rxjs/operators';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { ActivatedRoute } from '@angular/router';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PDL_EVENT_CTA_ID, PDL_PATH } from '@experience/reb-pdl/constants/pdl.constants';
import {
  Error,
  PdlDigitalDataEventObject,
} from '@experience/reb-pdl/interfaces/pdl-data.interface';

@Component({
  selector: 'tmo-global-error-message',
  templateUrl: './global-error-message.component.html',
})
export class GlobalErrorMessageComponent
  extends PDLBaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  /**
   * Subject to manage subscriptions
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * Boolean value to show/hide error details .
   */
  public showDetails: object;

  /**
   * Boolean value to maintain production flag .
   */
  public isProdEnabled: boolean;
  /**
   * Input value for fractional pages
   */
  @Input() public isFractionalPage?: boolean;
  /**
   * Variable holds final global API error numbers
   */
  public globalErrorApiNumbers: string[];
  /**
   * Variable holds final global API error object
   */
  public errorObject: GlobalErrorSubjectInterface;
  /**
   * Variable holds global API error numbers
   */
  private apiNumbers: string[];
  /**
   * Variable holds custom API error numbers
   */
  private customErrorApiNumbers: string[];
  /**
   * Variable hold API Errors
   */
  private ApiError: APIError[];
  /**
   * Set PDL event data for component
   */
  private eventData: PdlDigitalDataEventObject;

  @Input() public dormancyCheck?: boolean;

  constructor(
    private appConfig: AppConfig,
    private globalErrorService: GlobalErrorService,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService,
    private objectService: ObjectService
  ) {
    super(pdlService, routeSnapshot);
    this.ngUnsubscribe = new Subject<void>();
    this.initializeData();
  }

  /**
   * Initializes the error details component.
   * @returns void
   */
  public ngOnInit(): void {
    this.isProdEnabled = this.appConfig.getConfig('isProdEnabled');
  }

  /**
   * ngAfterViewInit for the component
   * @returns void.
   */
  public ngAfterViewInit(): void {
    this.globalErrorService.globalErrorObservable
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((apiErrors: APIError[]) => {
        if (apiErrors && apiErrors.length > 0) {
          this.ApiError = [...apiErrors, ...this.ApiError];
          this.ApiError.forEach((error: APIError) => {
            this.apiNumbers.push(error.apiNumber);
            if (
              this.errorObject[error.apiNumber] === null ||
              this.errorObject[error.apiNumber] === undefined
            ) {
              this.errorObject[error.apiNumber] = apiErrors;
            }
          });
          this.globalErrorApiNumbers = Array.from(new Set(this.apiNumbers));
          this.ApiError = this.globalErrorService.checkUserErrorMessage(this.ApiError);
          this.checkPDLData();
        }
      });
    this.globalErrorService.clearGlobalErrorObservable
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(() => {
        this.resetData();
      });
    this.globalErrorService.checkDuplicateErrorObservable
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((endpointConfig: EndpointConfigInterface) => {
        const index = this.globalErrorApiNumbers.indexOf(endpointConfig.name);
        if (index !== -1) {
          this.globalErrorApiNumbers = this.globalErrorApiNumbers.filter(
            (apiNumber: string) => apiNumber !== endpointConfig.name
          );
        }
      });
    this.globalErrorService.customErrorObservable
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((customApiErrors: APIError[]) => {
        if (customApiErrors && customApiErrors.length > 0) {
          customApiErrors.forEach((error: APIError) => {
            this.customErrorApiNumbers.push(error.apiNumber);
          });
          this.globalErrorApiNumbers = this.globalErrorApiNumbers.filter((apiNumber: string) => {
            return !this.customErrorApiNumbers.includes(apiNumber);
          });
          if (this.customErrorApiNumbers && this.customErrorApiNumbers.length > 0) {
            this.customErrorApiNumbers.forEach((apiNumber: string) => {
              if (this.errorObject[apiNumber]) {
                delete this.errorObject[apiNumber];
              }
            });
          }
        }
      });
  }

  /**
   *
   */
  private checkPDLData(): void {
    const pdlErrors: Error[] = [];
    this.globalErrorApiNumbers.forEach((apiNumber: string) => {
      this.errorObject[apiNumber].forEach((error: APIError) => {
        if (error.apiNumber === apiNumber && error.userMessage) {
          const pdlError: Error = {
            errorText: error.userMessage,
          };
          if (error.code || error.subStatusCode) {
            pdlError.errorCode =
              error.code && error.subStatusCode
                ? `${error.code} : ${error.subStatusCode}`
                : `${error.code || error.subStatusCode}`;
          }
          pdlErrors.push(pdlError);
        }
      });
    });
    if (pdlErrors.length > 0) {
      this.objectService.set(this.eventData, PDL_PATH.EVENT.ERROR, pdlErrors);
      this.pdlService.triggerCustomEvent(this.pdlPageComponentId, PDL_EVENT_CTA_ID.ERROR);
    }
  }

  /**
   * Initializes default data.
   * @returns void
   */
  private initializeData(): void {
    this.showDetails = {};
    this.apiNumbers = [];
    this.globalErrorApiNumbers = [];
    this.errorObject = {};
    this.customErrorApiNumbers = [];
    this.ApiError = [];
    this.eventData = {};
  }

  private resetData(): void {
    this.initializeData();
  }

  /**
   * Component lifecycle hook to destroy the component
   */
  public ngOnDestroy(): void {
    this.resetData();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
